<script lang="ts" setup>
import axios from 'axios'
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAuthStore } from '@/stores/auth-store'
import { DeliveryDescription } from '@/types/delivery-management'
import { image as imageHelper } from '@/utils/assets'
import { ResourceResponse, uuid } from '@/types/general'
import { imageViewerOpenKey } from '@/types/global-injection-keys'
import { useFileDownloader } from '@/hooks/use-file-downloader'

import MyButton from '@/components/my-components/MyButton.vue'
import MyModal from '@/components/my-components/MyModal.vue'
import MyImageViewer from '@/components/my-components/MyImageViewer.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'

export interface Props {
    modelValue: boolean
    deliveryDescriptionId: uuid
}

const props = defineProps<Props>()
const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
}>()

const { t } = useI18n()
const authStore = useAuthStore()

const downloadRoute = computed(() => {
    return window.route('dm.company.delivery-descriptions.download', {
        company: authStore.companyId,
        deliveryDescription: deliveryDescription.value?.id,
    })
})
const { downloading, downloadFile } = useFileDownloader()

const imageViewerOpen = inject(imageViewerOpenKey)!

const loading = ref(false)
const deliveryDescription = ref<DeliveryDescription>()

async function fetchDeliveryDescription() {
    loading.value = true
    try {
        const response = await axios.get<ResourceResponse<DeliveryDescription>>(
            window.route('dm.company.delivery-descriptions.show', {
                company: authStore.companyId,
                delivery_description: props.deliveryDescriptionId,
            }),
        )
        deliveryDescription.value = response.data.data
    } finally {
        loading.value = false
    }
}

function onClose() {
    if (imageViewerOpen.value) return

    emit('update:modelValue', false)
}

watch(
    () => props.modelValue,
    () => {
        if (!props.modelValue) return
        fetchDeliveryDescription()
    },
)
</script>

<template>
    <MyModal :value="props.modelValue" :max-width="1000" @close="onClose">
        <LoaderWrapper :visible="loading || downloading" />
        <template #title>
            <div class="flex justify-between items-start">
                <span
                    class="text-lg font-semibold uppercase text-primary-400"
                    v-text="t('deliveryDescription')"
                />
                <MyButton
                    v-if="deliveryDescription"
                    icon
                    plain
                    scheme="light"
                    size="small"
                    @click="() => downloadFile(downloadRoute)"
                >
                    <mdi:download />
                </MyButton>
            </div>
        </template>
        <div v-if="deliveryDescription">
            <div class="flex justify-between my-4">
                <div class="flex flex-col w-full space-y-3">
                    <div class="flex w-full space-x-2">
                        <div class="flex w-1/2 flex-col">
                            <span
                                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                                v-text="t('name')"
                            />
                            <span
                                class="text-md font-semibold text-primary-500 dark:text-primary-100 mb-4 break-all"
                                v-text="deliveryDescription.fields.name ?? '-'"
                            />
                        </div>

                        <div class="flex w-1/2 flex-col">
                            <span
                                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                                v-text="t('entrance')"
                            />
                            <span
                                class="text-md font-semibold text-primary-500 dark:text-primary-100 mb-4 break-all"
                                v-text="deliveryDescription.fields.entrance ?? '-'"
                            />
                        </div>
                    </div>

                    <div class="flex w-full space-x-2">
                        <div class="flex w-1/2 flex-col">
                            <span
                                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                                v-text="t('contactInformation')"
                            />
                            <span
                                class="text-md font-semibold text-primary-500 dark:text-primary-100 mb-4 whitespace-pre-line"
                                v-text="deliveryDescription.fields.contactInformation ?? '-'"
                            />
                        </div>
                        <div class="flex w-1/2 flex-col">
                            <span
                                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                                v-text="t('itemLocation')"
                            />
                            <span
                                class="text-md font-semibold text-primary-500 dark:text-primary-100 mb-4 break-all"
                                v-text="deliveryDescription.fields.itemLocation ?? '-'"
                            />
                        </div>
                    </div>

                    <div class="flex w-full space-x-2">
                        <div class="flex w-1/2 flex-col">
                            <span
                                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                                v-text="t('palletLocation')"
                            />
                            <span
                                class="text-md font-semibold text-primary-500 dark:text-primary-100 mb-4 break-all"
                                v-text="deliveryDescription.fields.palletLocation ?? '-'"
                            />
                        </div>
                        <div class="flex w-1/2 flex-col">
                            <span
                                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                                v-text="t('returnItemLocation')"
                            />
                            <span
                                class="text-md font-semibold text-primary-500 dark:text-primary-100 mb-4 break-all"
                                v-text="deliveryDescription.fields.returnItemLocation ?? '-'"
                            />
                        </div>
                    </div>

                    <div class="flex w-full space-x-2">
                        <div class="flex w-1/2 flex-col">
                            <span
                                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                                v-text="t('returnPackagingLocation')"
                            />
                            <span
                                class="text-md font-semibold text-primary-500 dark:text-primary-100 mb-4 break-all"
                                v-text="deliveryDescription.fields.returnPackagingLocation ?? '-'"
                            />
                        </div>
                        <div class="flex w-1/2 flex-col">
                            <span
                                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                                v-text="t('alarm')"
                            />
                            <span
                                class="text-md font-semibold text-primary-500 dark:text-primary-100 mb-4 break-all"
                                v-text="deliveryDescription.fields.alarm ?? '-'"
                            />
                        </div>
                    </div>

                    <div class="flex w-full space-x-2">
                        <div class="flex w-1/2 flex-col">
                            <span
                                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                                v-text="t('timeDemands')"
                            />
                            <span
                                class="text-md font-semibold text-primary-500 dark:text-primary-100 mb-4 break-all"
                                v-text="deliveryDescription.fields.timeDemands ?? '-'"
                            />
                        </div>
                        <div class="flex w-1/2 flex-col">
                            <span
                                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                                v-text="t('keyBox')"
                            />
                            <span
                                class="text-md font-semibold text-primary-500 dark:text-primary-100 mb-4 break-all"
                                v-text="deliveryDescription.fields.keyBox ?? '-'"
                            />
                        </div>
                    </div>
                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="t('note')"
                        />
                        <span
                            class="text-md font-semibold text-primary-500 dark:text-primary-100 mb-4 break-words"
                            v-text="deliveryDescription.fields.note ?? '-'"
                        />
                    </div>
                </div>
                <div class="flex w-full flex-col items-center">
                    <img
                        v-if="deliveryDescription.drawingPath"
                        :src="imageHelper(deliveryDescription?.drawingPath!, 'product')"
                        :alt="t('deliveryDescription')"
                        class="w-[400px] h-[400px] object-contain"
                    />
                </div>
            </div>
            <div v-if="deliveryDescription.images.length > 0" class="flex flex-col space-y-1.5">
                <span
                    class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                    v-text="t('images')"
                />
                <div class="flex flex-nowrap w-full">
                    <MyImageViewer class="grid grid-cols-4 gap-3">
                        <div
                            v-for="file in deliveryDescription.images"
                            :key="file.id"
                            class="relative aspect-square flex cursor-pointer justify-items-center overflow-hidden rounded-xl bg-primary-300"
                        >
                            <div
                                v-if="file.note"
                                v-tooltip="file.note"
                                class="absolute top-0 right-0 flex h-6 w-6 items-center justify-center rounded-bl-lg bg-primary-300 text-xs text-primary-50 shadow-lg"
                            >
                                <mdi:comment />
                            </div>
                            <img
                                class="w-auto object-fit"
                                :src="imageHelper(file.path!, 'thumbnail')"
                                :alt="file.note || ' '"
                                :data-src="imageHelper(file.path!, 'public')"
                            />
                        </div>
                    </MyImageViewer>
                </div>
            </div>
        </div>
    </MyModal>
</template>
